/**
 * NotificationDrawer — right-side panel listing the caller's notifications.
 *
 * Rows deep-link to their source record (the backend supplies a
 * workspace-aware path) and mark themselves read on click. "Mark all read"
 * clears the bell in one shot.
 */

import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import { Bell, Check, CheckCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Drawer } from '@/components/common/Drawer'
import { useHasPermission } from '@/hooks/useHasPermission'
import { Permissions } from '@/lib/permissions'
import {
  markAllNotificationsRead,
  markNotificationRead,
  useNotificationList,
} from '../hooks/useNotifications'
import { fetchInvoiceDetail } from '@/services/finance/financeService'
import { customerInvoicesPath, resolveNotificationDestination } from '../notificationDestination'
import type { NotificationItem } from '../types'

interface NotificationDrawerProps {
  open: boolean
  onClose: () => void
  /** Push a fresh unread count into the bell badge without waiting for the poll. */
  onUnreadCountChange: (count: number) => void
}

const TIER_DOT: Record<number, string> = {
  1: 'bg-red-500',
  2: 'bg-amber-500',
  3: 'bg-slate-400',
}

export function NotificationDrawer({ open, onClose, onUnreadCountChange }: NotificationDrawerProps) {
  const navigate = useNavigate()
  const hasFinanceAccess = useHasPermission(Permissions.FINANCE_ACCESS)
  // `/sales/customers` is gated too — a recipient with neither permission (pricing lead, ops
  // coordinator) needs the ungated Operations customer list instead.
  const hasSalesAccess = useHasPermission(Permissions.SALES_ACCESS)
  const {
    notifications,
    isLoading,
    serverUnreadCount,
    hasMore,
    loadMore,
    setNotifications,
    setServerUnreadCount,
  } = useNotificationList(open)
  // Server count is the truth for >1-page users (audit P5); local read
  // actions adjust it optimistically so the label updates instantly.
  const unreadCount = serverUnreadCount

  const handleRowClick = async (n: NotificationItem) => {
    if (!n.readAt) {
      setNotifications((prev) =>
        prev.map((x) =>
          x.notificationId === n.notificationId ? { ...x, readAt: new Date().toISOString() } : x,
        ),
      )
      setServerUnreadCount((c) => Math.max(0, c - 1))
      const fresh = await markNotificationRead(n.notificationId)
      if (fresh !== null) {
        setServerUnreadCount(fresh)
        onUnreadCountChange(fresh)
      }
    }
    // A finance deep link dead-ends at UnauthorizedModule for recipients without finance
    // access — and several of these events are addressed to the sales account owner on
    // purpose. resolveNotificationDestination maps those to the equivalent page they can
    // open; finance users always get the link exactly as written. See that module.
    const access = { finance: hasFinanceAccess, sales: hasSalesAccess }
    const { path, message, resolveCustomerFromInvoiceId } = resolveNotificationDestination(n, access)
    if (path) {
      onClose()
      navigate(path)
      return
    }
    if (resolveCustomerFromInvoiceId) {
      // Pre-`customerId` notification: look the invoice's party up rather than dead-ending.
      // Kept off the sync path so a slow or failed finance call never blocks the ones that
      // already know where they're going.
      try {
        const invoice = await fetchInvoiceDetail(resolveCustomerFromInvoiceId)
        const partyId = invoice?.partySnapshot?.partyId
        // Partner-side invoices resolve to a `prov_…` party the customer drawer cannot open.
        if (partyId?.startsWith('cust_')) {
          onClose()
          navigate(customerInvoicesPath(partyId, access.sales))
          return
        }
      } catch {
        // fall through to the message below
      }
      toast.info("This one is on a Finance screen you don't have access to.")
      return
    }
    if (message) toast.info(message)
  }

  const handleMarkAllRead = async () => {
    setNotifications((prev) =>
      prev.map((x) => (x.readAt ? x : { ...x, readAt: new Date().toISOString() })),
    )
    setServerUnreadCount(0)
    const fresh = await markAllNotificationsRead()
    onUnreadCountChange(fresh ?? 0)
  }

  return (
    <Drawer open={open} onClose={onClose} title="Notifications" width="default">
      <div className="flex flex-col h-full">
        {/* Actions bar */}
        <div className="flex items-center justify-between px-1 pb-2">
          <span className="text-xs text-muted-foreground">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </span>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={handleMarkAllRead}>
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </Button>
          )}
        </div>

        {isLoading && notifications.length === 0 ? (
          <div className="space-y-3 px-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Bell className="h-8 w-8 text-muted-foreground/40" />
            <p className="mt-3 text-sm font-medium">No notifications yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Task assignments, mentions, and alerts will appear here.
            </p>
          </div>
        ) : (
          <div className="-mx-1 divide-y divide-border/60">
            {notifications.map((n) => (
              <button
                key={n.notificationId}
                type="button"
                onClick={() => handleRowClick(n)}
                className={cn(
                  'flex w-full items-start gap-3 px-3 py-3 text-left transition-colors hover:bg-accent/50',
                  !n.readAt && 'bg-primary/[0.04]',
                )}
              >
                <span
                  className={cn(
                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                    n.readAt ? 'bg-transparent' : TIER_DOT[n.tier] ?? TIER_DOT[2],
                  )}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block truncate text-sm',
                      n.readAt ? 'text-muted-foreground' : 'font-medium text-foreground',
                    )}
                  >
                    {n.title}
                  </span>
                  {n.body && (
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">{n.body}</span>
                  )}
                  <span className="mt-0.5 block text-[11px] text-muted-foreground/70">
                    {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                  </span>
                </span>
                {n.readAt && <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />}
              </button>
            ))}
            {hasMore && (
              <div className="px-3 py-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  disabled={isLoading}
                  onClick={() => loadMore()}
                >
                  {isLoading ? 'Loading…' : 'Load older notifications'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  )
}
